"use client";

import { useCallback, useEffect, useState } from "react";

import { useRouter } from "next/navigation";

import { v4 as uuidv4 } from "uuid";

import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { useChat } from "@/contexts/ChatContext";
import { useCollections } from "@/contexts/CollectionsContext";
import { navigateToAgentResults } from "@/lib/results-navigation";
import { displayGenderToCategoryToken } from "@/utils/genderMapping";

import { HeroSearchBar } from "../navbar/navigation/HeroSearchBar";

import { CuratedSearchSection } from "./CuratedSearchSection";
import { LandingFooter } from "./LandingFooter";
import { ValuePropsSection } from "./ValuePropsSection";
import { WelcomeSection } from "./WelcomeSection";

interface LandingPageProps {
  initialLane?: string | null;
}

function LandingPageInner() {
  const { isOnboarded, userId, user, loading } = useAuth();
  const { clearChat, setChatCategory } = useChat();
  const { getOrCreateDefaultCart } = useCart();
  const { ensureDefaultCollection } = useCollections();

  const router = useRouter();

  const [mainSearchQuery, setMainSearchQuery] = useState("");

  useEffect(() => {
    if (loading) return;

    if (isOnboarded) {
      void (async () => {
        try {
          await Promise.all([
            getOrCreateDefaultCart().catch((e) => console.warn("Failed to ensure default cart", e)),
            ensureDefaultCollection().catch((e) =>
              console.warn("Failed to ensure default collection", e)
            ),
          ]);
        } catch {
          // Ignore initialization errors.
        }
      })();
    }
  }, [isOnboarded, userId, user, loading, getOrCreateDefaultCart, ensureDefaultCollection]);

  const handleSendMessage = useCallback(
    (message: string, genderDisplay?: string, existingChatId?: string) => {
      const q = message.trim();
      if (!q) return;

      clearChat();

      const chatId = existingChatId || uuidv4();
      const categoryToken = displayGenderToCategoryToken(genderDisplay);

      const chatData = {
        query: q,
        originalQuery: q,
        gender: genderDisplay,
        ...(categoryToken ? { category: categoryToken } : {}),
        userId,
        timestamp: Date.now(),
      };
      sessionStorage.setItem(`chat-${chatId}`, JSON.stringify(chatData));
      setChatCategory(chatId, categoryToken || undefined);

      navigateToAgentResults(router, chatId);
    },
    [router, userId, clearChat, setChatCategory]
  );

  const handleHeroSearchSubmit = useCallback(
    (gender?: string, query?: string, chatId?: string) => {
      const q = (query || mainSearchQuery).trim();
      if (q) {
        handleSendMessage(q, gender, chatId);
        setMainSearchQuery("");
      }
    },
    [mainSearchQuery, handleSendMessage]
  );

  return (
    <div className="flex min-h-screen flex-col items-center bg-white">
      <HeroSearchBar
        value={mainSearchQuery}
        onChange={setMainSearchQuery}
        onSubmit={handleHeroSearchSubmit}
      />
      <WelcomeSection />
      <CuratedSearchSection />
      <ValuePropsSection />
      <LandingFooter />
    </div>
  );
}

export default function LandingPage(_props: LandingPageProps) {
  return <LandingPageInner />;
}
